// R8 / roadmap 7 -- MOVED VERBATIM out of src/App.tsx, as areas are touched.
//
// N8-3's tarot photo panel: she photographs a spread she has already laid, tells us which spread it
// is and what she wants read, and the reading comes back. Not one line of it changed in the move --
// every dependency it had was already a shared module, which is why this one came out cleanly.
import { useMemo, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { Camera, Check, ImagePlus } from "lucide-react";
import { ANALYZE_SPREADS } from "../catalogs/tarot-spreads";
import { type LockInfo } from "../lib/tier-catalog";
import { READING_UNREACHABLE, readingAnswer, readingStatusLine } from "../lib/reading-answer";
import { postReadingToRoom, saveReadingNote, saveReadingState } from "../lib/saved-readings";
import { downloadReportPdf } from "../lib/downloads";
import { pdfAllowed } from "../readingPdf.js";
import { roomLabel, tarotShelfTitle } from "../rooms.js";
import { saveCopy } from "../saveCopy.js";
import { photoAccept } from "../uploadTypes.js";
import { preparePhotoForUpload } from "../photoUpload.js";
import { t } from "../strings.js";
import { AskLalaAboutButton } from "./AskLalaAboutButton";
import { ClosableReport, ReportPrintPortal } from "./DeliveredReading";
import { usePhotoBusy } from "./GenerationStrips";
import { MicButton } from "./MicButton";
import { TarotReadingView, type TarotReadingData } from "./TarotViews";

export function TarotAnalyzePanel({ onRequireMembership, language }: { onRequireMembership: (action: () => void, lock?: LockInfo) => void; language: string }) {
  const [preview, setPreview] = useState("");
  const [payload, setPayload] = useState<{ filename: string; data: string } | null>(null);
  const [context, setContext] = useState("");
  const [spread, setSpread] = useState("no-spread"); // N8-3: what she tells us she laid
  const [knownCards, setKnownCards] = useState("");
  const [askCards, setAskCards] = useState(false);
  const [localBusy, setBusy] = useState(false); const busy = usePhotoBusy("tarot", localBusy); // R225
  const [status, setStatus] = useState("");
  const [reading, setReading] = useState<TarotReadingData | null>(null);
  const [save, setSave] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [saveNote, setSaveNote] = useState("");
  const cameraRef = useRef<HTMLInputElement | null>(null);
  const libraryRef = useRef<HTMLInputElement | null>(null);
  // MIC-1: each mic is handed its own field so the live view keeps the newest dictated words visible.
  const contextRef = useRef<HTMLTextAreaElement | null>(null);
  const knownRef = useRef<HTMLTextAreaElement | null>(null);
  const touch = useMemo(
    () => typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(pointer: coarse)").matches,
    [],
  );

  // OV2-14: a phone photo is resized IN THE BROWSER before it is ever uploaded. The camera's original is
  // ~9.6MB, which base64s to a 12.86MB request the server refuses outright — that is what killed this
  // feature on every phone. src/photoUpload.js explains the numbers; here we just never send the original.
  async function pick(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    // Some phones hand over an empty type for a camera capture, so trust the extension too rather than
    // turning away a photo the member just took.
    if (file.type && !/^image\//.test(file.type)) { setStatus(t(language, "tarot.photo.notImage")); return; }
    setStatus(t(language, "tarot.photo.preparing"));
    const shot = await preparePhotoForUpload(file);
    if (!shot.base64) { setStatus(t(language, "tarot.photo.failed")); return; }
    setPreview(shot.dataUrl);
    setPayload({ filename: shot.filename, data: shot.base64 });
    setReading(null); setSave("idle"); setSaveNote(""); setAskCards(false); setKnownCards("");
    setStatus("");
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!payload?.data) { setStatus(t(language, "tarot.photo.needPhoto")); return; }
    onRequireMembership(() => { void run(); });
  }

  // R210: ONE failure sentence for this panel — its catch and its failure arm used to disagree.
  const analyzeCopy = { signIn: t(language, "tarot.analyze.signIn"), failed: t(language, "tarot.analyze.failed") };

  async function run() {
    setBusy(true);
    setReading(null); setSave("idle"); setSaveNote("");
    const stages = ["tarot.stage1", "tarot.stage2", "tarot.stage3", "type.stage4"].map((k) => t(language, k));
    let i = 0;
    setStatus(stages[0]);
    const timer = window.setInterval(() => { i = (i + 1) % stages.length; setStatus(stages[i]); }, 2600);
    try {
      const response = await fetch("/api/tarot/analyze", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ image: payload, context, knownCards, spread }),
      });
      const data = await response.json().catch(() => ({}));
      const answer = readingAnswer(response.status, data, (d) => (d.cards ? (d as TarotReadingData) : null));
      if (answer.kind === "confirm") { setAskCards(true); setStatus(answer.message); return; }
      if (answer.kind !== "delivered") { setAskCards(false); setStatus(readingStatusLine(answer, analyzeCopy)); return; }
      setAskCards(false);
      setReading(answer.body);
      setStatus("");
    } catch {
      setStatus(readingStatusLine(READING_UNREACHABLE, analyzeCopy));
    } finally {
      window.clearInterval(timer);
      setBusy(false);
    }
  }

  async function saveToRoom() {
    if (!reading) return;
    setSave("saving"); setSaveNote("");
    const answer = await postReadingToRoom({ type: "tarot", title: tarotShelfTitle(reading.spreadLabel), data: reading });
    setSave(saveReadingState(answer)); setSaveNote(saveReadingNote(answer, saveCopy(language, t(language, "tarot.saveFailed"))));
  }

  return (
    // N8-1: SECTION TWO of the room, standing on its own. It is not nested under the spreads and it does
    // not depend on them: a member who never pulls from the site can come straight here.
    <section className="tarot-section tarot-section--analyze tarot-analyze" id="analyze-your-reading">
      <p className="tarot-section__eyebrow">Section two</p>
      <h2 className="tarot-analyze__title">Analyze Your Reading</h2>
      <p className="tarot-analyze__blurb">
        Pulled your own cards? Photograph the spread just as it lies and Lala will read it for you, card by
        card, against whatever you were asking. You do not need to pull from the site first.
      </p>

      <form className="tarot-analyze__form reading-form" onSubmit={submit}>
        <input ref={cameraRef} type="file" accept={photoAccept()} capture="environment" className="tarot-analyze__file" onChange={pick} hidden />
        <input ref={libraryRef} type="file" accept={photoAccept()} className="tarot-analyze__file" onChange={pick} hidden />

        <button type="button" className="tarot-analyze__drop" onClick={() => (touch ? cameraRef : libraryRef).current?.click()}>
          {preview
            ? <img src={preview} alt="Your spread" className="tarot-analyze__preview" />
            : <span className="tarot-analyze__hint"><Camera size={26} /> Add a photo of your spread</span>}
        </button>

        <div className="tarot-analyze__picks">
          {touch ? (
            <button type="button" className="button button--ghost" onClick={() => cameraRef.current?.click()}>
              <Camera size={16} /> Take a photo
            </button>
          ) : null}
          <button type="button" className="button button--ghost" onClick={() => libraryRef.current?.click()}>
            <ImagePlus size={16} /> {t(language, touch ? "tarot.photo.library" : preview ? "tarot.photo.chooseAgain" : "tarot.photo.choose")}
          </button>
        </div>

        {/* N8-3: she tells Lala what she laid. A dropdown, not loose buttons, so the phone stays clean.
            The keys are the server's own spread keys, so the choice cannot drift from the engine. */}
        <label className="wide tarot-analyze__spread">
          <span>{t(language, "tarot.analyze.laid")}</span>
          <select value={spread} onChange={(event) => setSpread(event.target.value)}>
            {ANALYZE_SPREADS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </label>

        {/* MIC-1 (her order) — the second of the tarot room's writing boxes. `.mic-field` is the ONE
            declaration of "a textarea with the shared mic in its corner" (the journal's shape, given
            a name that is not the journal's), and no limits are passed because this box has its own
            designed height: W4-7's rule is that a field with a designed height takes the SCROLL half
            and must not be resized out from under its own layout. */}
        <label className="wide tarot-analyze__context">
          <span>{t(language, "tarot.analyze.about")}</span>
          <div className="mic-field">
            <textarea
              ref={contextRef}
              value={context}
              onChange={(event) => setContext(event.target.value)}
              placeholder={t(language, "tarot.analyze.aboutPlaceholder")}
            />
            <MicButton setValue={setContext} field={contextRef} />
          </div>
        </label>

        {/* Partial identification: she names what she can see and asks for the rest. Her typing wins,
            because she is the one holding the cards. */}
        {askCards ? (
          <label className="wide tarot-analyze__known">
            <span>{t(language, "tarot.analyze.unreadable")}</span>
            {/* MIC-1 — **AND THE THIRD BOX GETS IT TOO, WHICH IS ONE MORE THAN HER ORDER COUNTED.**
                Her clause says "BOTH text boxes … the question/situation box and any second input the
                room carries." The room carries THREE, and this is the one a member is most likely to
                want a mic on: it is filled in while her hands are holding the cards she is naming. */}
            <div className="mic-field">
              <textarea
                ref={knownRef}
                value={knownCards}
                onChange={(event) => setKnownCards(event.target.value)}
                placeholder={t(language, "tarot.analyze.unreadablePlaceholder")}
              />
              <MicButton setValue={setKnownCards} field={knownRef} />
            </div>
          </label>
        ) : null}

        <button className={`button button--primary${busy ? " is-generating" : ""}`} type="submit" disabled={busy || !payload}>
          {t(language, busy ? "tarot.analyze.going" : askCards ? "tarot.analyze.goWithCards" : "tarot.analyze.go")}
        </button>
        {status ? <p className={`find-your-type__status${busy ? " is-live" : ""}`}>{status}</p> : null}
      </form>

      {reading ? (
        <ClosableReport language={language} saved={save === "saved"} onClose={() => { setReading(null); setSave("idle"); setSaveNote(""); }}>
          <div className="chart-report__wrap">
            <TarotReadingView data={reading} />
            <ReportPrintPortal data={reading} />
            {/* 14-B6a: this toolbar keeps its OWN layout (the reader's bar, not the report's action
                row) and takes its SENTENCES from the one law, so the four words are still declared
                once for all nineteen surfaces. Layout is what differs here; copy is not. */}
            <div className="reader__toolbar">
              <span className="result-dest__shelf"><Check size={15} /> {t(language, "result.onShelf", { room: roomLabel("tarot") })}</span>
              <div className="chart-report__actions">
                {pdfAllowed(reading) ? <button type="button" onClick={() => downloadReportPdf(reading)}>{t(language, "reports.download")}</button> : null}
                <AskLalaAboutButton report={reading} kind="reading" />
                <button type="button" onClick={saveToRoom} disabled={save === "saving" || save === "saved"}>
                  {save === "saved" ? t(language, "result.saved") : save === "saving" ? t(language, "result.saving") : t(language, "result.save")}
                </button>
              </div>
            </div>
            {saveNote ? <p className="chart-report__save-note">{saveNote}</p> : null}
          </div>
        </ClosableReport>
      ) : null}
    </section>
  );
}
